import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useNetworkStatus } from '../../hooks/useNetworkStatus'
import { useAuthStore } from '../../stores/auth'
import { normalizePhone, isValidNormalizedPhone } from '@velorent/shared'

function applyPhoneMask(value: string): string {
  const digits = value.replace(/\D/g, '')
  let countryDigit = '7'
  let rest = digits
  if (digits.startsWith('7') || digits.startsWith('8')) {
    rest = digits.slice(1)
  } else if (digits.length > 0) {
    countryDigit = digits[0] ?? '7'
    rest = digits.slice(1)
  }
  const p1 = rest.slice(0, 3)
  const p2 = rest.slice(3, 6)
  const p3 = rest.slice(6, 8)
  const p4 = rest.slice(8, 10)
  let result = `+${countryDigit}`
  if (p1) result += ` ${p1}`
  if (p2) result += `-${p2}`
  if (p3) result += `-${p3}`
  if (p4) result += `-${p4}`
  return result
}

const schema = z.object({
  phone: z.string().min(10, 'Минимум 10 цифр'),
  customerName: z.string().optional(),
  documentNumber: z.string().optional(),
  paymentMethod: z.enum(['cash', 'card', 'transfer']).optional(),
  consent: z.literal(true, { errorMap: () => ({ message: 'Необходимо согласие на ПДн' }) }),
  isBooking: z.boolean().optional(),
  scheduledDate: z.string().optional(),
  scheduledTime: z.string().optional(),
  prepaymentAmount: z.coerce.number().min(0).optional(),
  discountPercent: z.coerce.number().min(0).max(100).optional(),
  priceOverride: z.coerce.number().min(0).optional(),
  priceOverrideReason: z.string().optional(),
  tariffId: z.string().uuid('Тариф не определён').optional(),
  pricingMode: z.enum(['hourly', 'daily']),
  plannedDuration: z.coerce.number().min(1).max(30),
}).refine((d) => d.isBooking || !!d.paymentMethod, { message: 'Выберите способ оплаты', path: ['paymentMethod'] })
.refine((d) => !d.isBooking || (d.scheduledDate && d.scheduledTime && d.prepaymentAmount !== undefined && d.prepaymentAmount >= 0), {
  message: 'Для бронирования укажите дату, время и предоплату',
  path: ['scheduledDate'],
}).refine((d) => d.isBooking || d.tariffId, { message: 'Выберите тариф', path: ['tariffId'] })
.refine((d) => d.isBooking || (d.customerName && d.customerName.trim().length > 0), {
  message: 'Укажите имя клиента',
  path: ['customerName'],
})
.refine((d) => d.isBooking || (d.documentNumber && d.documentNumber.trim().length > 0), {
  message: 'Укажите номер документа',
  path: ['documentNumber'],
})

type FormData = z.infer<typeof schema>

interface BikeTariff { id: string; name: string; hourlyRate: number; dailyRate?: number | null; color?: string | null; crossPointFee?: number | null }
interface BikeState {
  id: string; code: string; type?: string | null; model?: string; qrData: string
  status?: string
  tariffId?: string | null
  tariff?: BikeTariff | null
}
interface Tariff { id: string; name: string; bikeType: string; hourlyRate: number; dailyRate: number; crossPointFee?: number }

interface BookingState {
  id: string
  bike: { id: string; code: string; type?: string | null; tariffId?: string | null; tariff?: BikeTariff | null }
  customerPhone: string
  customerName?: string
  documentNumber?: string
  scheduledStartAt: string
  prepaymentAmount: string
}

interface FormSnapshot {
  phone?: string; customerName?: string; documentNumber?: string
  paymentMethod?: 'cash' | 'card' | 'transfer'
  pricingMode: 'hourly' | 'daily'; plannedDuration: number; prepaymentAmount?: number
  /** Сохраняем «открыто по QR» между шагами добавления великов (#1) */
  openedViaQr?: boolean
}

const STATUS_LABEL: Record<string, string> = {
  free: 'Свободен',
  reserved: 'Забронирован',
  in_use: 'В аренде',
  service: 'Обслуживание',
}

function getBikeTariffInfo(bike: BikeState, tariffs: Tariff[]): BikeTariff | null {
  // Always prefer the live tariff from the freshly-fetched ['tariffs'] list: the
  // tariff embedded in bike.tariff is cached with the bike and goes stale when the
  // owner changes a price, which made the new-rental estimate keep the OLD price.
  const tariffId = bike.tariffId ?? bike.tariff?.id
  if (tariffId) {
    const t = tariffs.find((t) => t.id === tariffId)
    // crossPointFee must be carried through — without it the «Сдадут в другой точке»
    // checkbox stays hidden (totalCrossPointFee === 0) even when the tariff has a fee (#2).
    if (t) return { id: t.id, name: t.name, hourlyRate: t.hourlyRate, dailyRate: t.dailyRate, crossPointFee: t.crossPointFee ?? null }
  }
  // Fallback only when the bike's tariff is not present in the list (e.g. inactive).
  if (bike.tariff) return bike.tariff
  return null
}

export default function RentalFormPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  // Manual price at open is owner-only now (managers use discount). Hidden otherwise.
  const canSetManualPrice = useAuthStore((s) => s.user?.role === 'owner')
  const state = location.state as {
    bike?: BikeState; bikes?: BikeState[]; booking?: BookingState; formSnapshot?: FormSnapshot; openedViaQr?: boolean
  } | null
  // Default to manual (false) when the flag is absent → backend flags requiresAttention (#1).
  // For multi-bike rentals the flag survives the "add bike" round-trip via formSnapshot.
  const openedViaQr = state?.openedViaQr ?? state?.formSnapshot?.openedViaQr ?? false

  // Support both single bike (old flow) and multiple bikes (new flow)
  const [bikes, setBikes] = useState<BikeState[]>(() => {
    if (state?.bikes?.length) return state.bikes
    if (state?.bike) return [state.bike]
    if (state?.booking?.bike) return [state.booking.bike as unknown as BikeState]
    return []
  })

  const booking = state?.booking
  const snapshot = state?.formSnapshot
  const { online } = useNetworkStatus()

  const { data: tariffs = [] } = useQuery<Tariff[]>({
    queryKey: ['tariffs'],
    queryFn: () => api.get('/tariffs').then((r) => r.data.tariffs ?? []),
  })

  const defaultValues: Partial<FormData> = {
    consent: true,
    pricingMode: snapshot?.pricingMode ?? 'hourly',
    plannedDuration: snapshot?.plannedDuration ?? 1,
    isBooking: false,
    phone: snapshot?.phone ?? (booking?.customerPhone ?? ''),
    customerName: snapshot?.customerName ?? (booking?.customerName ?? ''),
    documentNumber: snapshot?.documentNumber ?? (booking?.documentNumber ?? ''),
    paymentMethod: snapshot?.paymentMethod,
    prepaymentAmount: snapshot?.prepaymentAmount,
  }

  const { register, handleSubmit, watch, setValue, control, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const pricingMode = watch('pricingMode')
  const plannedDuration = watch('plannedDuration') ?? 1
  const tariffId = watch('tariffId')
  const isBooking = watch('isBooking')
  const customerName = watch('customerName') ?? ''
  const documentNumber = watch('documentNumber') ?? ''

  const lookupTimer = useRef<ReturnType<typeof setTimeout>>()
  const lastLookedUp = useRef('')
  const phoneValue = watch('phone')

  const handlePhoneLookup = useCallback((raw: string) => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current)
    const normalized = normalizePhone(raw)
    if (!isValidNormalizedPhone(normalized) || normalized === lastLookedUp.current) return
    lookupTimer.current = setTimeout(async () => {
      lastLookedUp.current = normalized
      try {
        const res = await api.get('/customers/lookup', { params: { phone: normalized } })
        const c = res.data.customer
        if (!c) return
        if (c.customerName && !watch('customerName')?.trim()) setValue('customerName', c.customerName)
        if (c.documentNumber && !watch('documentNumber')?.trim()) setValue('documentNumber', c.documentNumber)
      } catch { /* ignore lookup failures */ }
    }, 600)
  }, [watch, setValue])

  useEffect(() => {
    if (phoneValue && phoneValue.length >= 10 && !booking) handlePhoneLookup(phoneValue)
    return () => { if (lookupTimer.current) clearTimeout(lookupTimer.current) }
  }, [phoneValue, booking, handlePhoneLookup])

  // Auto-select tariff for first bike
  useEffect(() => {
    if (tariffs.length > 0 && bikes.length > 0 && !tariffId) {
      const firstBike = bikes[0]
      const firstBikeTariff = firstBike ? getBikeTariffInfo(firstBike, tariffs) : null
      if (firstBikeTariff) setValue('tariffId', firstBikeTariff.id)
    }
    if (booking) {
      setValue('phone', booking.customerPhone)
      setValue('customerName', booking.customerName ?? '')
      setValue('documentNumber', booking.documentNumber ?? '')
      setValue('prepaymentAmount', parseFloat(booking.prepaymentAmount) || 0)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffs.length, bikes.length, booking])

  // Per-bike price: each bike uses its own tariff
  const bikePrices = bikes.map((b) => {
    const tariff = getBikeTariffInfo(b, tariffs)
    if (!tariff) return 0
    return pricingMode === 'hourly'
      ? tariff.hourlyRate * plannedDuration
      : (tariff.dailyRate ?? 0) * plannedDuration
  })
  const totalEstimate = bikePrices.reduce((a, b) => a + b, 0)

  // Sync prepayment to total estimate (accounting for discount and manual price)
  const discountVal = Number(watch('discountPercent') ?? 0)
  const discountedEstimate = discountVal > 0 ? Math.round(totalEstimate * (1 - discountVal / 100)) : totalEstimate

  const [showPriceBlock, setShowPriceBlock] = useState(false)
  const priceOverrideVal = showPriceBlock ? Number(watch('priceOverride') || 0) : 0

  const [crossPointReturn, setCrossPointReturn] = useState(false)
  const prevCrossPointReturn = useRef(false)

  // Cross-point fee: sum across all selected bikes using their tariff's crossPointFee
  const totalCrossPointFee = bikes.reduce((sum, b) => {
    const t = getBikeTariffInfo(b, tariffs)
    return sum + Number(t?.crossPointFee ?? 0)
  }, 0)
  const hasCrossPointFee = totalCrossPointFee > 0

  // Auto-sync prepayment when tariff/duration/discount/priceOverride changes
  // Does NOT overwrite if only the cross-point checkbox changed (handled separately below)
  // Cross-point fee respects the rental discount — backend discounts it too (#18),
  // so the prepayment shown at open time must match (was charged full before).
  const discountedCrossFee = discountVal > 0
    ? Math.round(totalCrossPointFee * (1 - discountVal / 100))
    : totalCrossPointFee

  useEffect(() => {
    if (!isBooking && !booking) {
      const crossExtra = crossPointReturn ? discountedCrossFee : 0
      if (showPriceBlock && priceOverrideVal > 0) {
        setValue('prepaymentAmount', priceOverrideVal + crossExtra)
      } else if (totalEstimate > 0) {
        setValue('prepaymentAmount', discountedEstimate + crossExtra)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBooking, booking, discountedEstimate, showPriceBlock, priceOverrideVal, discountedCrossFee, setValue])

  // When cross-point checkbox toggles: add or subtract the fee from current value
  // preserving any manual edits the manager may have made
  useEffect(() => {
    if (prevCrossPointReturn.current === crossPointReturn) return
    prevCrossPointReturn.current = crossPointReturn
    if (isBooking || booking || totalCrossPointFee === 0) return
    const current = Number(watch('prepaymentAmount') ?? 0)
    setValue('prepaymentAmount', crossPointReturn ? current + discountedCrossFee : Math.max(0, current - discountedCrossFee))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossPointReturn])

  const [createdRentalId, setCreatedRentalId] = useState<string | null>(null)

  const submitForm = useMutation({
    mutationFn: async (data: FormData) => {
      const unavailableBike = !booking ? bikes.find((bike) => bike.status && bike.status !== 'free') : null
      if (unavailableBike) {
        const status = STATUS_LABEL[unavailableBike.status ?? ''] ?? unavailableBike.status
        throw Object.assign(new Error(`Велосипед #${unavailableBike.code} недоступен: ${status}. Выберите свободный велосипед.`), {
          response: { data: { error: { message: `Велосипед #${unavailableBike.code} недоступен: ${status}. Выберите свободный велосипед.` } } },
        })
      }
      if (showPriceBlock && (!data.priceOverrideReason || data.priceOverrideReason.trim().length === 0)) {
        throw Object.assign(new Error('Укажите причину изменения цены'), { response: { data: { error: { message: 'Укажите причину изменения цены' } } } })
      }
      const firstBike = bikes[0]
      const firstTariff = firstBike ? getBikeTariffInfo(firstBike, tariffs) : null
      const base = {
        bikeId: firstBike?.id,
        phone: data.phone,
        consent: data.consent,
        consentGiven: true,
        docVerified: customerName.trim().length > 0 && documentNumber.trim().length > 0,
        customerName: data.customerName || undefined,
        documentNumber: data.documentNumber || undefined,
      }
      if (data.isBooking && data.scheduledDate && data.scheduledTime) {
        const scheduledStartAt = new Date(`${data.scheduledDate}T${data.scheduledTime}:00`)
        return api.post('/rentals', {
          ...base,
          isBooking: true,
          scheduledStartAt: scheduledStartAt.toISOString(),
          prepaymentAmount: data.prepaymentAmount ?? 0,
        })
      }
      const prepayment = booking
        ? parseFloat(booking.prepaymentAmount) || 0
        : (Number(data.prepaymentAmount) || 0)
      const res = await api.post('/rentals', {
        ...base,
        tariffId: firstTariff?.id ?? data.tariffId,
        pricingMode: data.pricingMode,
        plannedDuration: data.plannedDuration,
        paymentMethod: data.paymentMethod,
        prepaymentAmount: prepayment,
        ...(data.discountPercent && data.discountPercent > 0 ? { discountPercent: data.discountPercent } : {}),
        ...(showPriceBlock && data.priceOverride != null ? { priceOverride: data.priceOverride, priceOverrideReason: data.priceOverrideReason || undefined } : {}),
        ...(booking?.id ? { existingBookingId: booking.id } : {}),
        openedViaQr,
      })
      const rentalId = res.data?.rental?.id
      // Add additional bikes sequentially
      if (rentalId && bikes.length > 1) {
        for (const extraBike of bikes.slice(1)) {
          const extraTariff = getBikeTariffInfo(extraBike, tariffs)
          if (extraTariff) {
            await api.post(`/rentals/${rentalId}/add-bike`, {
              bikeId: extraBike.id,
              tariffId: extraTariff.id,
            })
          }
        }
      }
      return res
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['bookings'] })
      qc.invalidateQueries({ queryKey: ['rentals'] })
      const rental = res.data?.rental
      if (rental?.id && !res.data?.booking) {
        setCreatedRentalId(rental.id)
      } else {
        navigate('/manager/rentals')
      }
    },
  })

  const getFormSnapshot = (): FormSnapshot => ({
    phone: watch('phone'),
    customerName: watch('customerName'),
    documentNumber: watch('documentNumber'),
    paymentMethod: watch('paymentMethod'),
    pricingMode: watch('pricingMode'),
    plannedDuration: watch('plannedDuration') ?? 1,
    prepaymentAmount: watch('prepaymentAmount'),
    openedViaQr,
  })

  const handleAddMoreBikes = () => {
    navigate('/manager/scan', {
      state: {
        addBikeToNewRental: true,
        existingBikes: bikes,
        existingBikeIds: bikes.map((b) => b.id),
        formSnapshot: getFormSnapshot(),
      },
    })
  }

  const handleRemoveBike = (bikeId: string) => {
    setBikes((prev) => prev.filter((b) => b.id !== bikeId))
  }

  if (createdRentalId) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <h1 className="font-bold text-gray-900 text-sm">Аренда создана</h1>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4">
          <span className="text-5xl">✅</span>
          <p className="text-gray-700 font-semibold text-lg">Аренда открыта</p>
          <div className="text-sm text-gray-500 space-y-1">
            {bikes.map((b) => {
              const t = getBikeTariffInfo(b, tariffs)
              return <p key={b.id}>#{b.code}{t ? ` · ${t.name}` : ''}{b.model ? ` · ${b.model}` : ''}</p>
            })}
          </div>
          <button
            onClick={() => navigate('/manager/rentals')}
            className="w-full border-2 border-gray-300 text-gray-600 py-3 rounded-2xl font-semibold text-sm"
          >
            К активным арендам
          </button>
        </div>
      </div>
    )
  }

  if (bikes.length === 0) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-400 p-1 text-xl">←</button>
          <h1 className="font-bold text-gray-900 text-sm">Открыть аренду</h1>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4">
          <span className="text-4xl">🚲</span>
          <p className="text-gray-600 font-medium">Велосипед не выбран</p>
          <p className="text-sm text-gray-400">Отсканируйте QR-код или введите код велосипеда</p>
          <button onClick={() => navigate('/manager/scan')} className="bg-brand-600 text-white px-6 py-3 rounded-2xl font-semibold">
            Перейти к сканеру
          </button>
        </div>
      </div>
    )
  }

  const selectedTariff = tariffs.find((t) => t.id === tariffId)
  const unavailableBikes = !booking ? bikes.filter((bike) => bike.status && bike.status !== 'free') : []

  return (
    <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-gray-50">
      <header className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-400 p-1 text-xl">←</button>
        <div>
          <h1 className="font-bold text-gray-900 text-sm">Открыть аренду</h1>
          <p className="text-xs text-gray-400">{bikes.length} ед. инвентаря</p>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <form className="p-4 space-y-4">
        {unavailableBikes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700">
            {unavailableBikes.map((bike) => {
              const status = STATUS_LABEL[bike.status ?? ''] ?? bike.status
              return <p key={bike.id}>Велосипед #{bike.code} недоступен для открытия: {status}.</p>
            })}
            <p className="mt-1 font-medium">Вернитесь к сканеру и выберите свободный велосипед.</p>
          </div>
        )}

        {/* Phone */}
        <div className="bg-white rounded-2xl p-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700">Телефон клиента</label>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <input
                type="tel"
                value={field.value ? applyPhoneMask(field.value) : ''}
                onChange={e => field.onChange(applyPhoneMask(e.target.value))}
                onBlur={field.onBlur}
                placeholder="+7 XXX-XXX-XX-XX"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            )}
          />
          {errors.phone && <p className="text-red-500 text-xs">{errors.phone.message}</p>}

          <label className="block text-sm font-medium text-gray-700 mt-2">
            Имя клиента {!isBooking && <span className="text-red-500">*</span>}
          </label>
          <input
            {...register('customerName')}
            placeholder="Иван Иванов"
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          {errors.customerName && <p className="text-red-500 text-xs">{errors.customerName.message}</p>}

          <label className="block text-sm font-medium text-gray-700">
            Номер документа {!isBooking && <span className="text-red-500">*</span>}
          </label>
          <input
            {...register('documentNumber')}
            placeholder="Серия и номер паспорта"
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          {errors.documentNumber && <p className="text-red-500 text-xs">{errors.documentNumber.message}</p>}
        </div>

        {/* Bikes list */}
        <div className="bg-white rounded-2xl p-4 space-y-3">
          <p className="text-sm font-medium text-gray-700">Инвентарь</p>
          <div className="space-y-2">
            {bikes.map((b, i) => {
              const tariffInfo = getBikeTariffInfo(b, tariffs)
              const price = bikePrices[i] ?? 0
              return (
                <div key={b.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                  <div>
                    <p className="font-semibold text-sm flex items-center gap-1.5">
                      {tariffInfo?.color && <span style={{ width: 10, height: 10, backgroundColor: tariffInfo.color, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />}
                      #{b.code}{b.model ? ` · ${b.model}` : ''}
                    </p>
                    <p className="text-xs text-gray-500">
                      {tariffInfo?.name ?? <span className="text-orange-500">Тариф не найден</span>}
                      {price > 0 && <span className="ml-1 text-brand-600 font-medium">· {price.toLocaleString('ru-RU')} ₽</span>}
                    </p>
                  </div>
                  {bikes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveBike(b.id)}
                      className="text-gray-400 hover:text-red-500 text-xl leading-none px-1"
                    >
                      ×
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          {!isBooking && (
            <button
              type="button"
              onClick={handleAddMoreBikes}
              className="w-full border-2 border-dashed border-brand-300 text-brand-600 py-3 rounded-xl text-sm font-medium hover:bg-brand-50 active:bg-brand-50"
            >
              + Добавить инвентарь
            </button>
          )}
        </div>

        {/* Banner when converting existing booking to rental */}
        {booking && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
            <span className="text-xl">📌</span>
            <div>
              <p className="text-sm font-semibold text-amber-800">Открытие аренды по бронированию</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Предоплата {booking.prepaymentAmount} ₽ уже учтена. Выберите тариф и откройте аренду.
              </p>
            </div>
          </div>
        )}

        {/* Booking mode */}
        {!booking && <div className="bg-white rounded-2xl p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input {...register('isBooking')} type="checkbox" className="w-5 h-5 rounded text-brand-600" />
            <span className="text-sm font-medium text-gray-700">📌 Режим бронирования</span>
          </label>
          {isBooking && (
            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Дата</label>
                  <input
                    {...register('scheduledDate')}
                    type="date"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Время</label>
                  <input
                    {...register('scheduledTime')}
                    type="time"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Сумма предоплаты (₽)</label>
                <input
                  {...register('prepaymentAmount')}
                  type="number"
                  min={0}
                  step={100}
                  placeholder="500"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm"
                />
              </div>
            </div>
          )}
        </div>}

        {/* Tariff — shown only when no tariff from bike */}
        {!isBooking && !selectedTariff && (
          <div className="bg-white rounded-2xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Тариф (первый велосипед)</p>
            <div className="space-y-2">
              {tariffs.map((t) => (
                <label key={t.id} className="flex items-center gap-3 cursor-pointer">
                  <input {...register('tariffId')} type="radio" value={t.id} className="w-4 h-4 text-brand-600" />
                  <span className="text-sm">{t.name} — {t.hourlyRate}₽/ч · {t.dailyRate}₽/сутки</span>
                </label>
              ))}
            </div>
            {errors.tariffId && <p className="text-red-500 text-xs mt-1">{errors.tariffId.message}</p>}
          </div>
        )}
        {!isBooking && selectedTariff && <input type="hidden" {...register('tariffId')} />}

        {/* Duration & Mode */}
        {!isBooking && <div className="bg-white rounded-2xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Режим оплаты</label>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {(['hourly', 'daily'] as const).map((m) => (
              <label key={m} className={`flex items-center justify-center gap-1 rounded-xl py-2.5 border cursor-pointer transition-colors ${pricingMode === m ? 'bg-brand-600 border-brand-600 text-white' : 'border-gray-200 text-gray-600'}`}>
                <input {...register('pricingMode')} type="radio" value={m} className="hidden" />
                <span className="text-sm font-medium">{m === 'hourly' ? '⏱ По часам' : '🌙 На сутки'}</span>
              </label>
            ))}
          </div>

          {pricingMode === 'hourly' ? (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-1">Кол-во часов (1–23)</label>
              <input
                {...register('plannedDuration')}
                type="number" min={1} max={23}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </>
          ) : (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">Кол-во суток</label>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 5, 7].map((d) => (
                  <label
                    key={d}
                    className={`flex flex-col items-center justify-center rounded-xl py-3 border cursor-pointer transition-colors ${plannedDuration === d ? 'bg-brand-600 border-brand-600 text-white' : 'border-gray-200 text-gray-600'}`}
                  >
                    <Controller
                      control={control}
                      name="plannedDuration"
                      render={({ field }) => (
                        <input
                          type="radio"
                          value={d}
                          checked={field.value === d}
                          onChange={() => field.onChange(d)}
                          className="hidden"
                        />
                      )}
                    />
                    <span className="text-lg font-bold">{d}</span>
                    <span className="text-xs">{d === 1 ? 'сутки' : 'сут.'}</span>
                  </label>
                ))}
              </div>
              <input
                {...register('plannedDuration')}
                type="number" min={1} max={30}
                placeholder="Другое"
                className="mt-2 w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-center"
              />
              <p className="text-xs text-gray-400 mt-1">Или введите любое число суток (до 30)</p>
            </>
          )}
        </div>}

        {/* Payment method */}
        <div className="bg-white rounded-2xl p-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Способ оплаты <span className="text-red-500">*</span></p>
          <div className="grid grid-cols-3 gap-2">
            {([['cash', '💵', 'Наличные'], ['card', '💳', 'Безнал'], ['transfer', '📲', 'Перевод']] as const).map(([val, icon, label]) => {
              const checked = watch('paymentMethod') === val
              return (
                <label key={val} className={`flex flex-col items-center justify-center gap-1 rounded-xl py-3 border cursor-pointer transition-colors ${checked ? 'bg-brand-600 border-brand-600 text-white' : 'border-gray-200 text-gray-600'}`}>
                  <input {...register('paymentMethod')} type="radio" value={val} className="hidden" />
                  <span className="text-xl">{icon}</span>
                  <span className="text-xs font-medium">{label}</span>
                </label>
              )
            })}
          </div>
          {errors.paymentMethod && <p className="text-red-500 text-xs mt-1">{errors.paymentMethod.message}</p>}
        </div>

        {/* Consent */}
        <div className="bg-white rounded-2xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input {...register('consent')} type="checkbox" defaultChecked className="w-5 h-5 rounded mt-0.5 text-brand-600" />
            <span className="text-sm text-gray-600">
              Клиент даёт <strong>согласие на обработку персональных данных</strong> в соответствии с ФЗ-152
            </span>
          </label>
          {errors.consent && <p className="text-red-500 text-xs mt-1">{errors.consent.message}</p>}
        </div>

        {/* Price override block — owner only (managers use discount instead) */}
        {!isBooking && canSetManualPrice && (
          <div className="bg-white rounded-2xl p-4">
            {!showPriceBlock ? (
              <button
                type="button"
                onClick={() => {
                  setShowPriceBlock(true)
                  setValue('priceOverride', discountedEstimate || totalEstimate)
                }}
                className="w-full text-sm font-medium text-amber-600 border border-amber-300 rounded-xl py-2.5 hover:bg-amber-50 transition-colors"
              >
                ✏️ Изменение цены аренды
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-amber-700">Ручная цена</p>
                  <button type="button" onClick={() => { setShowPriceBlock(false); setValue('priceOverride', undefined as unknown as number); setValue('priceOverrideReason', '') }} className="text-xs text-gray-400 hover:text-gray-600">Отмена</button>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Стоимость аренды (₽)</label>
                  <input
                    {...register('priceOverride')}
                    type="number"
                    min={0}
                    step={50}
                    className="w-full border border-amber-300 rounded-xl px-4 py-3 text-lg font-bold text-center focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Комментарий (обязательно)</label>
                  <textarea
                    {...register('priceOverrideReason')}
                    rows={2}
                    placeholder="Причина изменения цены..."
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <p className="text-xs text-amber-600">Аренда попадёт в «Требует внимания» для проверки владельцем</p>
              </div>
            )}
          </div>
        )}
        </form>
      </div>

      <div className="shrink-0 bg-white border-t border-gray-200 p-4 pb-safe max-h-[48vh] overflow-y-auto">
        {!isBooking && (
          <div className="mb-3 space-y-2">
            {/* Discount field */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-24 shrink-0">Скидка (%)</label>
              <input
                {...register('discountPercent')}
                type="number"
                min={0}
                max={100}
                step={5}
                placeholder="0"
                className="w-24 border border-gray-300 rounded-xl px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
              {(() => {
                const disc = Number(watch('discountPercent') ?? 0)
                if (disc > 0 && totalEstimate > 0) {
                  const discounted = Math.round(totalEstimate * (1 - disc / 100))
                  return <span className="text-sm text-green-600 font-semibold">→ {discounted.toLocaleString('ru-RU')} ₽</span>
                }
                return null
              })()}
            </div>
            {hasCrossPointFee && (
              <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={crossPointReturn}
                  onChange={(e) => setCrossPointReturn(e.target.checked)}
                  className="w-5 h-5 rounded text-orange-500 mt-0.5 shrink-0"
                />
                <div>
                  <p className="text-sm font-medium text-orange-800">Сдадут в другой точке</p>
                  <p className="text-xs text-orange-600 mt-0.5">
                    Межточечный сбор: +{totalCrossPointFee.toLocaleString('ru-RU')} ₽
                    {bikes.length > 1 && (
                      <span className="block">
                        {bikes.map((b) => {
                          const fee = Number(getBikeTariffInfo(b, tariffs)?.crossPointFee ?? 0)
                          return fee > 0 ? `#${b.code}: ${fee.toLocaleString('ru-RU')} ₽` : null
                        }).filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </p>
                </div>
              </label>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">Предоплата/оплата (₽)</label>
            <input
              {...register('prepaymentAmount')}
              type="number"
              min={0}
              step={50}
              placeholder={discountedEstimate > 0 ? `≈ ${discountedEstimate} ₽` : '0'}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-lg font-bold text-center focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
            {crossPointReturn && totalCrossPointFee > 0 && (
              <p className="text-xs text-orange-600 text-center">
                Включает межточечный сбор {totalCrossPointFee.toLocaleString('ru-RU')} ₽
              </p>
            )}
            {bikes.length > 1 && totalEstimate > 0 && (
              <p className="text-xs text-gray-400 mt-1 text-center">
                Итого: {bikes.map((b, i) => {
                  const t = getBikeTariffInfo(b, tariffs)
                  return t ? `#${b.code} ${bikePrices[i]?.toLocaleString('ru-RU')}₽` : null
                }).filter(Boolean).join(' + ')}
              </p>
            )}
          </div>
        )}
        {!online && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-sm text-amber-700 mb-2 text-center">
            ⚡ Нет подключения
          </div>
        )}
        <button
          type="button"
          onClick={handleSubmit((d) => submitForm.mutate(d))}
          disabled={submitForm.isPending || !online || unavailableBikes.length > 0}
          className="w-full bg-brand-600 text-white py-4 rounded-2xl font-bold text-base disabled:opacity-60"
        >
          {submitForm.isPending
            ? (isBooking ? 'Бронируем...' : 'Открываем...')
            : (isBooking ? '📌 Забронировать' : `🚲 Открыть аренду${bikes.length > 1 ? ` (${bikes.length} ед.)` : ''}`)}
        </button>
        {submitForm.error && (
          <p className="text-red-500 text-xs text-center mt-2">
            {(submitForm.error as { response?: { data?: { error?: { message: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
          </p>
        )}
      </div>
    </div>
  )
}
